// DOCX export of an article (Article.js "Download .docx").
// Builders extracted verbatim from handleDownloadDoc; behavior pinned by docxExport.test.js.
import { Document, Packer, Paragraph, TextRun, HeadingLevel, ImageRun, WidthType, AlignmentType, Table, TableRow, TableCell } from 'docx';
import { saveAs } from 'file-saver';

// Helper function to extract plain text from HTML
export const extractDocxText = (html) => {
  if (!html) return '';
  // Remove HTML tags but preserve line breaks
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
};

// Helper function to parse text with bold/italic formatting
export const parseFormattedText = (html) => {
  if (!html) return [];
  const text = extractDocxText(html);
  // Simple parsing - split by bold tags if present
  const parts = [];
  const boldRegex = /<b>(.*?)<\/b>|<strong>(.*?)<\/strong>/gi;
  let match;
  let lastIndex = 0;

  while ((match = boldRegex.exec(html)) !== null) {
    // Add text before bold
    if (match.index > lastIndex) {
      const beforeText = extractDocxText(html.substring(lastIndex, match.index));
      if (beforeText) {
        parts.push(new TextRun(beforeText));
      }
    }
    // Add bold text
    const boldText = match[1] || match[2];
    if (boldText) {
      parts.push(new TextRun({
        text: extractDocxText(boldText),
        bold: true
      }));
    }
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < html.length) {
    const remainingText = extractDocxText(html.substring(lastIndex));
    if (remainingText) {
      parts.push(new TextRun(remainingText));
    }
  }

  // If no formatting found, return plain text
  if (parts.length === 0) {
    return [new TextRun(text)];
  }

  return parts;
};

/**
 * EditorJS blocks -> docx paragraphs/tables (a leading H1 is skipped; the title is added separately).
 */
export const buildDocxBlockChildren = (blocks) => {
  // Convert EditorJS blocks to DOCX paragraphs
  const docxChildren = [];

  blocks.forEach((block, blockIndex) => {
    // Pomiń pierwszy nagłówek H1 – tytuł jest już dodany osobno
    if (blockIndex === 0 && block.type === 'header' && block.data.level === 1) return;

    switch (block.type) {
      case 'header':
        const headerText = extractDocxText(block.data.text);
        if (headerText) {
          let headingLevel;
          switch (block.data.level) {
            case 1:
              headingLevel = HeadingLevel.HEADING_1;
              break;
            case 2:
              headingLevel = HeadingLevel.HEADING_2;
              break;
            case 3:
              headingLevel = HeadingLevel.HEADING_3;
              break;
            case 4:
              headingLevel = HeadingLevel.HEADING_4;
              break;
            case 5:
              headingLevel = HeadingLevel.HEADING_5;
              break;
            case 6:
              headingLevel = HeadingLevel.HEADING_6;
              break;
            default:
              headingLevel = HeadingLevel.HEADING_2;
          }
          docxChildren.push(
            new Paragraph({
              text: headerText,
              heading: headingLevel,
              spacing: { after: 200 }
            })
          );
        }
        break;
      case 'paragraph':
        const paragraphText = block.data.text;
        if (paragraphText) {
          const textParts = parseFormattedText(paragraphText);
          docxChildren.push(
            new Paragraph({
              children: textParts,
              spacing: { after: 200 }
            })
          );
        }
        break;
      case 'list':
        block.data.items.forEach((item) => {
          const itemText = extractDocxText(item);
          if (itemText) {
            const textParts = parseFormattedText(item);
            docxChildren.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: block.data.style === 'ordered' ? '• ' : '• ',
                    bold: true
                  }),
                  ...textParts
                ],
                spacing: { after: 100 },
                indent: { left: 400 }
              })
            );
          }
        });
        break;
      case 'quote':
        const quoteText = extractDocxText(block.data.text);
        if (quoteText) {
          docxChildren.push(
            new Paragraph({
              text: `"${quoteText}"`,
              spacing: { after: 200 },
              indent: { left: 400 },
              italics: true
            })
          );
        }
        break;
      case 'table':
        if (block.data.content && block.data.content.length > 0) {
          const withHeadings = block.data.withHeadings === true;
          const numCols = block.data.content[0].length;
          const totalWidth = 10000;
          const colWidth = Math.floor(totalWidth / numCols);
          const columnWidths = Array(numCols).fill(colWidth);
          if (numCols * colWidth < totalWidth) columnWidths[0] += totalWidth - numCols * colWidth;
          const tableRows = block.data.content.map((row, rowIndex) => {
            const cells = row.map((cellContent) => {
              const cellText = extractDocxText(cellContent);
              const isHeader = withHeadings && rowIndex === 0;
              return new TableCell({
                children: [
                  new Paragraph({
                    children: [new TextRun({ text: cellText || ' ', bold: isHeader })]
                  })
                ]
              });
            });
            return new TableRow({ children: cells });
          });
          docxChildren.push(
            new Table({
              rows: tableRows,
              width: { size: 100, type: WidthType.PERCENTAGE },
              columnWidths
            })
          );
          docxChildren.push(new Paragraph({ text: '', spacing: { after: 200 } }));
        }
        break;
      default:
        // For other block types, try to extract text content
        if (block.data.text) {
          const plainText = extractDocxText(block.data.text);
          if (plainText.trim()) {
            docxChildren.push(
              new Paragraph({
                text: plainText,
                spacing: { after: 200 }
              })
            );
          }
        }
    }
  });
  return docxChildren;
};

/**
 * Appends the centered thumbnail image (max 500px wide). Errors are logged and ignored.
 */
export const appendThumbnailParagraph = async (documentChildren, thumbnailData) => {
  // Add thumbnail image if available
  if (thumbnailData && thumbnailData.url) {
    try {
      // Fetch the image
      const imageResponse = await fetch(thumbnailData.url);
      if (imageResponse.ok) {
        const imageBlob = await imageResponse.blob();
        const imageArrayBuffer = await imageBlob.arrayBuffer();

        // Get image dimensions for proper sizing (max width 500px, maintain aspect ratio)
        const img = new Image();
        const imageUrl = URL.createObjectURL(imageBlob);
        await new Promise((resolve, reject) => {
          img.onload = () => {
            URL.revokeObjectURL(imageUrl);
            const maxWidth = 500;
            const aspectRatio = img.height / img.width;
            const width = Math.min(img.width, maxWidth);
            const height = width * aspectRatio;

            documentChildren.push(
              new Paragraph({
                children: [
                  new ImageRun({
                    data: imageArrayBuffer,
                    transformation: {
                      width: width,
                      height: height,
                    },
                  }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 400 },
              })
            );
            resolve();
          };
          img.onerror = () => {
            URL.revokeObjectURL(imageUrl);
            reject(new Error('Failed to load image'));
          };
          img.src = imageUrl;
        });
      }
    } catch (imageError) {
      console.warn('Could not add thumbnail to document:', imageError);
      // Continue without thumbnail if there's an error
    }
  }
};

/**
 * Full document body: thumbnail, title, content, meta section and FAQ.
 */
export const buildArticleDocumentChildren = async ({ outputData, thumbnailData, title, metaTitle, metaDescription, faqItems }) => {
  const docxChildren = buildDocxBlockChildren(outputData.blocks);

  // Prepare document children
  const documentChildren = [];

  await appendThumbnailParagraph(documentChildren, thumbnailData);

  // Add title
  documentChildren.push(
    new Paragraph({
      text: title || 'Article',
      heading: HeadingLevel.HEADING_1,
      spacing: { after: 400 }
    })
  );

  // Add content
  documentChildren.push(...docxChildren);

  // Meta (bez HTML)
  if (metaTitle || metaDescription) {
    documentChildren.push(
      new Paragraph({ text: '', spacing: { after: 200 } }),
      new Paragraph({ text: 'Meta', heading: HeadingLevel.HEADING_2, spacing: { after: 200 } })
    );
    if (metaTitle) documentChildren.push(new Paragraph({ text: 'Title: ' + metaTitle, spacing: { after: 100 } }));
    if (metaDescription) documentChildren.push(new Paragraph({ text: 'Description: ' + metaDescription, spacing: { after: 200 } }));
  }

  // FAQ – odpowiedzi bez <p> (extractText)
  if (faqItems && faqItems.length > 0) {
    documentChildren.push(
      new Paragraph({ text: '', spacing: { after: 200 } }),
      new Paragraph({ text: 'FAQ', heading: HeadingLevel.HEADING_2, spacing: { after: 200 } })
    );
    faqItems.forEach((faq) => {
      const q = (faq.question ?? faq.q ?? '').trim();
      const a = extractDocxText(String(faq.answer ?? faq.a ?? ''));
      if (q) documentChildren.push(new Paragraph({ children: [new TextRun({ text: 'Q: ', bold: true }), new TextRun({ text: q, bold: true })], spacing: { after: 100 } }));
      if (a) documentChildren.push(new Paragraph({ children: [new TextRun({ text: 'A: ' }), new TextRun({ text: a })], spacing: { after: 200 } }));
    });
  }

  return documentChildren;
};

export const buildDocxFileName = (title) => `${(title || 'article').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.docx`;

/**
 * Builds the .docx and triggers the browser download.
 */
export const downloadArticleDocx = async ({ outputData, thumbnailData, title, metaTitle, metaDescription, faqItems }) => {
  const documentChildren = await buildArticleDocumentChildren({
    outputData,
    thumbnailData,
    title,
    metaTitle,
    metaDescription,
    faqItems,
  });

  // Create DOCX document
  const doc = new Document({
    sections: [{
      properties: {},
      children: documentChildren
    }]
  });

  // Generate and download DOCX file
  const blob = await Packer.toBlob(doc);
  const fileName = buildDocxFileName(title);
  saveAs(blob, fileName);

  console.log('✅ Document downloaded successfully');
};
